import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import {
  LayoutDashboard, CreditCard, Mail, Users, LogOut, Euro, TrendingUp,
  Send, Trash2, ShieldCheck, Disc3, Download, Images,
} from "lucide-react";
import { GalleryManager } from "@/components/admin/GalleryManager";

const fmt = (n) => `${(n ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [metrics, setMetrics] = useState(null);
  const [payments, setPayments] = useState([]);
  const [subs, setSubs] = useState([]);
  const [regs, setRegs] = useState([]);
  const [campaign, setCampaign] = useState({ subject: "", content: "", image_url: "", cta_label: "", cta_url: "", is_public: true });
  const [uploadingImg, setUploadingImg] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);

  const cfg = { headers: authHeaders };

  const loadAll = useCallback(async () => {
    try {
      const [m, p, s, r, c] = await Promise.all([
        api.get("/admin/metrics", cfg),
        api.get("/admin/payments", cfg),
        api.get("/admin/newsletter/subscribers", cfg),
        api.get("/admin/registrations", cfg),
        api.get("/admin/newsletter/campaigns", cfg),
      ]);
      setMetrics(m.data); setPayments(p.data); setSubs(s.data); setRegs(r.data); setCampaigns(c.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/admin/login");
    }
  }, [authHeaders]); // eslint-disable-line

  useEffect(() => {
    if (user === false) { navigate("/admin/login"); return; }
    if (user && user.role === "admin") loadAll();
  }, [user, loadAll, navigate]);

  const EMPTY_CAMPAIGN = { subject: "", content: "", image_url: "", cta_label: "", cta_url: "", is_public: true };
  const campaignPayload = () => ({
    subject: campaign.subject, content: campaign.content, is_public: campaign.is_public !== false,
    image_url: campaign.image_url || null, cta_label: campaign.cta_label || null, cta_url: campaign.cta_url || null,
  });

  const toggleCampaignPublic = async (c) => {
    try {
      await api.patch(`/admin/newsletter/campaigns/${c.id}`, { is_public: !c.is_public }, cfg);
      toast.success(!c.is_public ? "Actu publiée sur le site" : "Actu retirée du site");
      loadAll();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const uploadNewsletterImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/newsletter/image", fd, { ...cfg, headers: { ...cfg.headers, "Content-Type": "multipart/form-data" } });
      setCampaign((c) => ({ ...c, image_url: data.url }));
      toast.success("Image ajoutée à la newsletter");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploadingImg(false); }
  };

  const sendNewsletter = async () => {
    if (!campaign.subject || !campaign.content) { toast.error("Sujet et contenu requis"); return; }
    if (campaign.cta_url && !/^https:\/\//i.test(campaign.cta_url)) { toast.error("Le lien du bouton doit commencer par https://"); return; }
    const active = subs.filter((s) => s.active !== false).length;
    if (!window.confirm(`Envoyer cette newsletter à ${active} abonné(s) actif(s) ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", campaignPayload(), cfg);
      toast.success(`Envoi lancé vers ${data.recipients} abonné(s). Suivi dans l'historique.`);
      setCampaign(EMPTY_CAMPAIGN);
      setTimeout(loadAll, 4000);
      loadAll();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const sendTest = async () => {
    if (!campaign.subject || !campaign.content) { toast.error("Sujet et contenu requis"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/newsletter/test", campaignPayload(), cfg);
      toast.success(`Email de test envoyé à ${data.to}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const anonymize = async (id) => {
    if (!window.confirm("Anonymiser définitivement les données de cet adhérent (RGPD) ?")) return;
    try {
      await api.delete(`/admin/members/${id}`, cfg);
      toast.success("Données anonymisées (RGPD)");
      loadAll();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const deleteSub = async (id) => {
    try { await api.delete(`/admin/newsletter/subscribers/${id}`, cfg); loadAll(); toast.success("Abonné supprimé"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const rows = [["Prénom", "Nom", "Email", "Formule", "Cours", "Montant", "Paiement", "Statut", "Date"]];
    regs.forEach((r) => rows.push([
      r.first_name, r.last_name, r.email, r.formula_name,
      (r.courses || []).join(" | "), r.amount, r.installments === 3 ? "3x" : "1x",
      r.payment_status, r.created_at,
    ]));
    const csv = rows.map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ajdr-adherents-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  if (!user || user === false) return <div className="min-h-screen bg-ink flex items-center justify-center text-cream/50 font-mono2">Chargement…</div>;

  const NAV = [
    { key: "overview", label: "Vue d'ensemble", icon: LayoutDashboard },
    { key: "payments", label: "Paiements", icon: CreditCard },
    { key: "members", label: "Adhérents", icon: Users },
    { key: "newsletter", label: "Newsletter", icon: Mail },
    { key: "gallery", label: "Galerie", icon: Images },
  ];

  return (
    <div className="min-h-screen bg-ink flex flex-col lg:flex-row" data-testid="admin-dashboard-container">
      {/* sidebar */}
      <aside className="lg:w-64 shrink-0 bg-ink-2 border-b lg:border-b-0 lg:border-r border-white/5 p-5 flex lg:flex-col gap-2 lg:min-h-screen">
        <div className="flex items-center gap-2 mb-4 lg:mb-8 mr-auto lg:mr-0">
          <Disc3 className="w-7 h-7 text-mustard" />
          <span className="font-display font-black">AJDR<span className="text-mustard">.</span></span>
        </div>
        <nav className="flex lg:flex-col gap-1 flex-1 overflow-x-auto">
          {NAV.map((n) => (
            <button key={n.key} data-testid={`admin-nav-${n.key}`} onClick={() => setTab(n.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-sub whitespace-nowrap transition ${tab === n.key ? "bg-mustard text-ink font-semibold" : "text-cream/70 hover:bg-white/5"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }}
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm text-cream/60 hover:text-destructive transition">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </aside>

      {/* main */}
      <main className="flex-1 p-5 md:p-8 overflow-x-hidden">
        {tab === "overview" && (
          <div data-testid="admin-overview">
            <Header title="Vue d'ensemble" subtitle="Pilotage de la saison 2026/2027" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <Stat icon={Euro} label="Encaissé" value={fmt(metrics?.revenue_collected)} accent />
              <Stat icon={TrendingUp} label="Attendu (total)" value={fmt(metrics?.revenue_expected)} />
              <Stat icon={Users} label="Adhérents payés" value={metrics?.members_count ?? 0} />
              <Stat icon={Mail} label="Abonnés newsletter" value={metrics?.newsletter_count ?? 0} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <Stat icon={CreditCard} label="En attente de paiement" value={metrics?.pending_count ?? 0} />
              <Stat icon={CreditCard} label="Paiements en 3x" value={metrics?.installments_count ?? 0} />
              <Stat icon={Euro} label="Reste à encaisser" value={fmt(metrics?.revenue_pending)} />
              <Stat icon={Users} label="Inscriptions totales" value={metrics?.registrations_total ?? 0} />
            </div>

            <div className="mt-8 glass rounded-2xl p-6">
              <h3 className="font-sub font-semibold mb-4">Répartition par cours</h3>
              <div className="space-y-3">
                {(metrics?.by_course || []).map((c) => {
                  const max = Math.max(...(metrics?.by_course || []).map((x) => x.count), 1);
                  return (
                    <div key={c.course} className="flex items-center gap-3">
                      <span className="w-40 shrink-0 text-sm text-cream/70 truncate">{c.course}</span>
                      <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-mustard rounded-full" style={{ width: `${(c.count / max) * 100}%` }} />
                      </div>
                      <span className="font-mono2 text-sm text-mustard w-8 text-right">{c.count}</span>
                    </div>
                  );
                })}
                {(!metrics?.by_course || metrics.by_course.length === 0) && (
                  <p className="text-cream/40 text-sm font-mono2">Aucune inscription payée pour le moment.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "payments" && (
          <div data-testid="admin-payments-view">
            <Header title="Suivi des paiements & encaissements" subtitle="Toutes les transactions Stripe" />
            <div className="mt-6 glass rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-payments-table">
                  <thead className="bg-white/5 text-cream/50 font-mono2 text-xs uppercase">
                    <tr>
                      {["Adhérent", "Email", "Formule", "Cours", "Montant", "Mode", "Statut"].map((h) => (
                        <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 whitespace-nowrap">{p.first_name} {p.last_name}</td>
                        <td className="px-4 py-3 text-cream/60">{p.email}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{p.formula_name}</td>
                        <td className="px-4 py-3 text-cream/60 max-w-[220px] truncate">{(p.courses || []).join(", ")}</td>
                        <td className="px-4 py-3 font-mono2 whitespace-nowrap">{fmt(p.amount)}{p.installments === 3 && <span className="text-cream/40"> / {fmt(p.total_amount)}</span>}</td>
                        <td className="px-4 py-3"><span className="font-mono2 text-xs">{p.installments === 3 ? "3x" : "1x"}</span></td>
                        <td className="px-4 py-3"><StatusBadge status={p.payment_status} /></td>
                      </tr>
                    ))}
                    {payments.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-cream/40 font-mono2">Aucune transaction.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "members" && (
          <div data-testid="admin-members-view">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <Header title="Adhérents" subtitle="Base de données RGPD — droit à l'effacement" />
              <button data-testid="admin-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border border-mustard/40 text-mustard hover:bg-mustard hover:text-ink transition">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
            <div className="mt-6 glass rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-members-table">
                  <thead className="bg-white/5 text-cream/50 font-mono2 text-xs uppercase">
                    <tr>
                      {["Nom", "Email", "Tél", "Formule", "Statut", "RGPD"].map((h) => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {regs.map((r) => (
                      <tr key={r.id} data-testid={`member-row-${r.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 whitespace-nowrap">{r.first_name} {r.last_name}</td>
                        <td className="px-4 py-3 text-cream/60">{r.email}</td>
                        <td className="px-4 py-3 text-cream/60">{r.phone || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{r.formula_name}</td>
                        <td className="px-4 py-3"><StatusBadge status={r.payment_status} /></td>
                        <td className="px-4 py-3">
                          {r.anonymized ? <span className="text-xs text-cream/40 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Anonymisé</span> :
                            <button data-testid={`anonymize-${r.id}`} onClick={() => anonymize(r.id)} className="text-destructive/80 hover:text-destructive inline-flex items-center gap-1 text-xs">
                              <Trash2 className="w-3.5 h-3.5" /> Anonymiser
                            </button>}
                        </td>
                      </tr>
                    ))}
                    {regs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-cream/40 font-mono2">Aucun adhérent.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "newsletter" && (
          <div data-testid="admin-newsletter-manager">
            <Header title="Gestion Newsletter" subtitle={`${subs.filter((s) => s.active !== false).length} abonné(s) actif(s) sur ${subs.length} — envoi via Resend, lien de désabonnement automatique`} />
            <div className="grid lg:grid-cols-2 gap-6 mt-6">
              <div className="glass rounded-2xl p-6">
                <h3 className="font-sub font-semibold mb-4">Rédiger & envoyer</h3>
                <label className="block mb-3">
                  <span className="text-xs text-cream/50">Sujet</span>
                  <input data-testid="newsletter-subject" value={campaign.subject} onChange={(e) => setCampaign({ ...campaign, subject: e.target.value })}
                    className="mt-1 w-full rounded-lg bg-ink border border-white/10 px-3 py-2.5 text-cream focus:border-mustard focus:outline-none" placeholder="Ex: La Block Party revient !" />
                </label>
                <label className="block mb-4">
                  <span className="text-xs text-cream/50">Contenu</span>
                  <textarea data-testid="newsletter-content" rows={7} value={campaign.content} onChange={(e) => setCampaign({ ...campaign, content: e.target.value })}
                    className="mt-1 w-full rounded-lg bg-ink border border-white/10 px-3 py-2.5 text-cream focus:border-mustard focus:outline-none resize-none" placeholder="Ton message aux abonnés… (chaque abonné reçoit un lien de désabonnement personnel)" />
                </label>

                <div className="rounded-xl border border-white/5 bg-ink/60 p-4 mb-4 space-y-3">
                  <p className="font-mono2 text-[11px] uppercase tracking-wider text-cream/40">Image d'en-tête (optionnel)</p>
                  {campaign.image_url ? (
                    <div className="relative rounded-lg overflow-hidden border border-white/10">
                      <img src={campaign.image_url} alt="" data-testid="newsletter-image-preview" className="w-full max-h-40 object-cover" />
                      <button data-testid="newsletter-image-remove" onClick={() => setCampaign({ ...campaign, image_url: "" })}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-ink/80 text-cream/70 hover:bg-destructive hover:text-white flex items-center justify-center transition"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <input data-testid="newsletter-image-file" type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={uploadingImg}
                      onChange={(e) => uploadNewsletterImage(e.target.files?.[0])}
                      className="w-full text-sm text-cream/70 file:mr-3 file:rounded-full file:border-0 file:bg-mustard file:px-4 file:py-2 file:text-ink file:font-semibold disabled:opacity-60" />
                  )}
                  {uploadingImg && <p className="text-xs text-cream/50 font-mono2">Téléversement…</p>}
                </div>

                <div className="rounded-xl border border-white/5 bg-ink/60 p-4 mb-4">
                  <p className="font-mono2 text-[11px] uppercase tracking-wider text-cream/40 mb-3">Bouton d'action (optionnel)</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs text-cream/50">Texte du bouton</span>
                      <input data-testid="newsletter-cta-label" value={campaign.cta_label} maxLength={60} onChange={(e) => setCampaign({ ...campaign, cta_label: e.target.value })}
                        className="mt-1 w-full rounded-lg bg-ink border border-white/10 px-3 py-2.5 text-cream focus:border-mustard focus:outline-none" placeholder="En savoir plus" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-cream/50">Lien (https://…)</span>
                      <input data-testid="newsletter-cta-url" value={campaign.cta_url} onChange={(e) => setCampaign({ ...campaign, cta_url: e.target.value })}
                        className="mt-1 w-full rounded-lg bg-ink border border-white/10 px-3 py-2.5 text-cream focus:border-mustard focus:outline-none" placeholder="https://ajdrfactory.fr/…" />
                    </label>
                  </div>
                </div>
                <label className="flex items-start gap-3 rounded-xl border border-white/5 bg-ink/60 p-4 mb-4 cursor-pointer">
                  <input data-testid="newsletter-public-toggle" type="checkbox" checked={campaign.is_public !== false}
                    onChange={(e) => setCampaign({ ...campaign, is_public: e.target.checked })} className="mt-1 accent-[#DD3733]" />
                  <span>
                    <span className="block text-sm text-cream">Publier aussi dans les « Actus » du site</span>
                    <span className="block text-xs text-cream/50 mt-0.5">Décochez pour un message réservé aux abonnés (infos internes, séjours, tarifs…). La liste des abonnés n'est jamais publiée.</span>
                  </span>
                </label>

                <div className="flex flex-wrap gap-3">
                  <button data-testid="newsletter-send-btn" onClick={sendNewsletter} disabled={sending}
                    className="btn-mustard rounded-full px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-60">
                    <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer aux abonnés"}
                  </button>
                  <button data-testid="newsletter-test-btn" onClick={sendTest} disabled={testing}
                    className="rounded-full px-5 py-2.5 inline-flex items-center gap-2 text-sm border border-mustard/40 text-mustard hover:bg-mustard hover:text-ink transition disabled:opacity-60">
                    <Mail className="w-4 h-4" /> {testing ? "Envoi…" : "M'envoyer un test"}
                  </button>
                </div>

                <h3 className="font-sub font-semibold mt-8 mb-3">Historique des envois</h3>
                <div className="space-y-2 max-h-[260px] overflow-y-auto" data-testid="newsletter-campaigns">
                  {campaigns.map((c) => (
                    <div key={c.id} data-testid={`campaign-${c.id}`} className="rounded-lg bg-ink border border-white/5 px-3 py-2 text-sm flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-cream/90 truncate">{c.subject}</p>
                        <p className="text-[11px] font-mono2 text-cream/40">
                          {new Date(c.created_at).toLocaleString("fr-FR")}{c.image_url ? " · image" : ""}{c.cta_url ? " · bouton" : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button data-testid={`campaign-public-${c.id}`} onClick={() => toggleCampaignPublic(c)}
                          title={c.is_public ? "Visible dans les Actus du site — cliquer pour retirer" : "Réservé aux abonnés — cliquer pour publier"}
                          className={`font-mono2 text-[10px] uppercase rounded-full px-2 py-0.5 border transition ${c.is_public ? "text-mustard border-mustard/40 hover:bg-mustard hover:text-ink" : "text-cream/40 border-white/10 hover:text-cream"}`}>
                          {c.is_public ? "Public" : "Privé"}
                        </button>
                        <span className={`font-mono2 text-xs rounded-full px-2.5 py-0.5 border ${c.status === "sending" ? "text-mustard border-mustard/40" : "text-cream/60 border-white/10"}`}>
                          {c.status === "sending" ? "En cours" : "Envoyé"} {c.sent ?? 0}/{c.recipients ?? 0}
                        </span>
                      </div>
                    </div>
                  ))}
                  {campaigns.length === 0 && <p className="text-cream/40 text-sm font-mono2">Aucune newsletter envoyée.</p>}
                </div>
              </div>

              <div className="glass rounded-2xl p-6">
                <h3 className="font-sub font-semibold mb-4">Abonnés</h3>
                <div className="space-y-2 max-h-[560px] overflow-y-auto">
                  {subs.map((s) => (
                    <div key={s.id} data-testid={`subscriber-${s.id}`} className={`flex items-center justify-between gap-3 rounded-lg bg-ink border border-white/5 px-3 py-2 text-sm ${s.active === false ? "opacity-50" : ""}`}>
                      <div className="min-w-0">
                        <p className="text-cream/80 truncate">{s.first_name ? <span className="text-cream">{s.first_name} · </span> : null}{s.email}</p>
                        <p className="text-[11px] font-mono2 text-cream/40">
                          {s.active === false ? `Désabonné le ${new Date(s.unsubscribed_at).toLocaleDateString("fr-FR")}` : `Abonné le ${new Date(s.subscribed_at).toLocaleDateString("fr-FR")}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span data-testid={`subscriber-status-${s.id}`} className={`font-mono2 text-[10px] uppercase rounded-full px-2 py-0.5 border ${s.active === false ? "text-cream/40 border-white/10" : "text-mustard border-mustard/40"}`}>
                          {s.active === false ? "Inactif" : "Actif"}
                        </span>
                        <button data-testid={`delete-sub-${s.id}`} onClick={() => deleteSub(s.id)} className="text-cream/40 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                  {subs.length === 0 && <p className="text-cream/40 text-sm font-mono2">Aucun abonné.</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div data-testid="admin-gallery-view">
            <Header title="Galerie Battles & Spectacles" subtitle="Photos (stockage sécurisé) et vidéos YouTube/Vimeo affichées sur le site" />
            <GalleryManager cfg={cfg} />
          </div>
        )}
      </main>
    </div>
  );
}

function Header({ title, subtitle }) {
  return (
    <div>
      <h1 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{title}</h1>
      <p className="text-cream/50 text-sm mt-1">{subtitle}</p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl p-5 border ${accent ? "bg-mustard/10 border-mustard/30" : "bg-ink-2 border-white/5"}`}>
      <Icon className={`w-5 h-5 ${accent ? "text-mustard" : "text-cream/40"}`} />
      <div className="mt-3 font-display font-black text-xl md:text-2xl">{value}</div>
      <div className="mt-1 font-mono2 text-[11px] uppercase tracking-wider text-cream/45">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: ["Payé", "bg-emerald/20 text-emerald-bright border-emerald/40"],
    pending: ["En attente", "bg-mustard/15 text-mustard border-mustard/40"],
    completed: ["Payé", "bg-emerald/20 text-emerald-bright border-emerald/40"],
    initiated: ["Initié", "bg-white/5 text-cream/50 border-white/10"],
    expired: ["Expiré", "bg-destructive/15 text-destructive border-destructive/40"],
    failed: ["Échec", "bg-destructive/15 text-destructive border-destructive/40"],
  };
  const [label, cls] = map[status] || [status, "bg-white/5 text-cream/50 border-white/10"];
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-mono2 border ${cls}`}>{label}</span>;
}
